/** The Lifecycle tab's "State" block (tenant retirement CONTRACT §8.7): one row per thing that
 *  decides what can happen to the tenant, each with its state in words, what it means, and the
 *  action that changes it (`console_tenant_lifecycle_manage`, behind `PermissionGate`):
 *
 *  - Lifecycle — active / closed / releasing / released / restoring, with when it was closed,
 *    reopened and released (and the code it gave up). *Request close…* on an active tenant,
 *    *Request reopen…* on a closed one and on a released one whose code is free again
 *    (`reopen_available`), while no request is open; otherwise why not (`reopen_blockers`: the
 *    tenants now holding the code, linked) and that a reopen under a new code is done by hand.
 *    After a reopen, its manual follow-ups (`lifecycle.reprovision.manual`) as a checklist.
 *  - Request — the open request: who asked, when and why; *Approve…*, *Reject…*, *Withdraw…*
 *    while it is pending.
 *  - Job — while a release or a restore runs (or failed, the tenant still releasing / restoring):
 *    its steps, done / running / failed, with each finished step's counts, and the failure.
 *    *Retry…* runs a failed job again from its stored step; on a running one it is offered for a
 *    job whose worker died (the backend refuses while the job is alive).
 *  - Approval — two-person approval on or off.
 *  - Release gate — the waiting period after a close (`earliest_release_at`) and, on a closed
 *    tenant, what blocks a code release now (`release_blockers`) with what each needs, from its
 *    extras: the waiting period's dates, the offboarding items in the way (linking to the
 *    Offboarding tab), the work still running. *Request release…* on a closed tenant while no
 *    request is open; its dialog shows the gate again, with a superuser's override of the waiting
 *    period.
 *
 *  Every write is audited server-side. */
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import DoNotDisturbOnOutlinedIcon from '@mui/icons-material/DoNotDisturbOnOutlined';
import EngineeringOutlinedIcon from '@mui/icons-material/EngineeringOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import HowToVoteOutlinedIcon from '@mui/icons-material/HowToVoteOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import { Box, Button, Link } from '@mui/material';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { reopenFollowUps, type ReopenFollowUp, type TenantLifecycleState } from '@/api/tenantLifecycle';
import { TENANT_REQUEST_KIND_LABELS, TENANT_REQUEST_STATUS_LABELS, type TenantRequest } from '@/api/tenantRequests';
import { PermissionGate } from '@/components/PermissionGate';
import { StateChip, StateRow, type ChipTone } from '@/features/tour-operators/sandbox/SandboxStateSection';
import { When } from '@/features/tour-operators/sandbox/When';

import { ApproveLifecycleDialog } from './ApproveLifecycleDialog';
import { LifecycleBlockerList } from './LifecycleBlockerList';
import { LifecycleRequestQuote } from './lifecycleFormat';
import { currentJobRequest, jobOf, LifecycleJobSteps, type JobFollow } from './lifecycleJob';
import { LIFECYCLE_HELP, LIFECYCLE_LABEL } from './lifecycleWords';
import { RejectLifecycleDialog } from './RejectLifecycleDialog';
import { RequestLifecycleDialog, type RequestableKind } from './RequestLifecycleDialog';
import { RetryLifecycleDialog } from './RetryLifecycleDialog';
import { WithdrawLifecycleDialog } from './WithdrawLifecycleDialog';

export const MANAGE_PERM = 'console_tenant_lifecycle_manage';

const LIFECYCLE_TONE: Record<TenantLifecycleState['lifecycle'], ChipTone> = {
  active: 'success',
  closed: 'warning',
  releasing: 'info',
  released: 'default',
  restoring: 'info',
};

export function LifecycleStateSection({
  state,
  follow,
}: {
  state: TenantLifecycleState;
  /** How the tab follows a running job (absent: not followed). */
  follow?: JobFollow;
}) {
  return (
    <Box>
      <LifecycleRow state={state} />
      <OpenRequestRow state={state} />
      <JobRow state={state} follow={follow} />
      <ApprovalRow state={state} />
      <ReleaseGateRow state={state} />
    </Box>
  );
}

// --------------------------------------------------------------------------- lifecycle

/** A closed or released tenant may be reopened when the backend says so (a released one only
 *  while its codes are free again). */
const reopenable = (state: TenantLifecycleState) =>
  state.lifecycle === 'closed' || state.lifecycle === 'released';

function LifecycleRow({ state }: { state: TenantLifecycleState }) {
  const [kind, setKind] = useState<RequestableKind | null>(null);
  const idle = state.open_request == null;
  const requestable: RequestableKind | null = !idle
    ? null
    : state.lifecycle === 'active'
      ? 'close'
      : reopenable(state) && state.reopen_available
        ? 'reopen'
        : null;
  const reopenRefused = idle && reopenable(state) && !state.reopen_available;
  const codeTaken = state.reopen_blockers.some((b) => b.code === 'code_taken');
  // The newest applied request, when it is a reopen: what it left to do by hand.
  const lastApplied = state.lifecycle === 'active' ? state.recent_requests.find((r) => r.status === 'applied') : undefined;
  const followUps = lastApplied?.kind === 'reopen' ? reopenFollowUps(lastApplied) : [];
  return (
    <>
      <StateRow
        icon={<DoNotDisturbOnOutlinedIcon fontSize="small" />}
        label="Lifecycle"
        chip={<StateChip tone={LIFECYCLE_TONE[state.lifecycle]} label={LIFECYCLE_LABEL[state.lifecycle]} />}
        actions={
          requestable && (
            <PermissionGate perm={MANAGE_PERM}>
              <Button
                size="small"
                variant="outlined"
                color={requestable === 'close' ? 'error' : 'primary'}
                onClick={() => setKind(requestable)}
              >
                {requestable === 'close' ? 'Request close…' : 'Request reopen…'}
              </Button>
            </PermissionGate>
          )
        }
      >
        {LIFECYCLE_HELP[state.lifecycle]}
        {state.closed_at && (
          <Box>
            {state.lifecycle === 'active' ? 'Last closed' : 'Closed'} <When iso={state.closed_at} />.
          </Box>
        )}
        {state.reopened_at && (
          <Box>
            Reopened <When iso={state.reopened_at} />.
          </Box>
        )}
        {(state.released_at || state.released_code) && (
          <Box>
            Code released{state.released_at ? ' ' : ''}
            {state.released_at && <When iso={state.released_at} />}
            {state.released_code ? `; it was ${state.released_code}` : ''}. Its code now:{' '}
            <Box component="span" sx={{ fontFamily: 'monospace' }}>
              {state.code}
            </Box>
            .
          </Box>
        )}
        {reopenRefused && (
          <Box sx={{ mt: 0.5 }}>
            It cannot be reopened now:
            <LifecycleBlockerList
              topId={state.tour_operator_id}
              blockers={state.reopen_blockers}
              label="What blocks a reopen now"
            />
            {state.lifecycle === 'released' && codeTaken && (
              <Box sx={{ mt: 0.5 }}>
                Reopening it under a new code is done by hand — see the tenant retirement runbook.
              </Box>
            )}
          </Box>
        )}
        {followUps.length > 0 && <ReopenFollowUps items={followUps} />}
      </StateRow>
      <RequestLifecycleDialog state={state} kind={kind} onClose={() => setKind(null)} />
    </>
  );
}

/** Short labels of the follow-up keys the backend sends (an unknown key is spelled out). */
const FOLLOW_UP_LABEL: Record<string, string> = {
  sign_in: 'Sign-in',
  vendor_webhooks: 'Vendor webhooks',
  vendor_webhooks_by_hand: 'Webhooks set up by hand',
  twilio_numbers: 'Twilio numbers',
  email_domain: 'E-mail domain',
  email_domain_deleted: 'E-mail domain (deleted)',
  firebase_testers: 'Firebase testers',
  firebase_project: 'Firebase project',
  mobile_apps: 'Mobile apps',
  jira_labels: 'Jira labels',
  helpdesk_users: 'Help-desk users',
  image_import_refs: 'Image import lists',
  undo_by_hand: 'Done by hand at offboarding',
  airbnb: 'Airbnb',
  api_keys: 'API keys',
  restech_connections: 'Booking-system connections',
};

const followUpLabel = (what: string) =>
  FOLLOW_UP_LABEL[what] ?? (what ? what.charAt(0).toUpperCase() + what.slice(1).replace(/_/g, ' ') : '');

/** What a reopen could not re-provision, as a checklist to work through by hand. */
function ReopenFollowUps({ items }: { items: ReopenFollowUp[] }) {
  return (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ color: 'text.primary', fontWeight: 500 }}>Still to do by hand after the reopen:</Box>
      <Box
        component="ul"
        aria-label="Reopen follow-ups"
        sx={{ m: 0, mt: 0.25, p: 0, listStyle: 'none', display: 'grid', gap: 0.25 }}
      >
        {items.map((f) => {
          const label = followUpLabel(f.what);
          return (
            <Box
              component="li"
              key={`${f.what}:${f.detail}`}
              data-follow-up={f.what}
              sx={{ display: 'flex', gap: 0.75 }}
            >
              <CheckBoxOutlineBlankIcon aria-hidden sx={{ fontSize: 16, mt: '2px', flex: 'none' }} />
              <span>
                {label && (
                  <Box component="span" sx={{ color: 'text.primary' }}>
                    {label}
                  </Box>
                )}
                {label && f.detail && ' — '}
                {f.detail}
              </span>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// --------------------------------------------------------------------------- open request

function OpenRequestRow({ state }: { state: TenantLifecycleState }) {
  const r = state.open_request;
  const [dialog, setDialog] = useState<'approve' | 'reject' | 'withdraw' | null>(null);
  const chip = r ? (
    <StateChip
      tone={r.status === 'pending' ? 'warning' : 'info'}
      label={`${TENANT_REQUEST_KIND_LABELS[r.kind]} — ${TENANT_REQUEST_STATUS_LABELS[r.status].toLowerCase()}`}
    />
  ) : (
    <StateChip tone="default" label="None open" />
  );
  const pending = r?.status === 'pending';
  const target = (d: typeof dialog): TenantRequest | null => (r && dialog === d ? r : null);
  return (
    <>
      <StateRow
        icon={<HowToVoteOutlinedIcon fontSize="small" />}
        label="Request"
        chip={chip}
        actions={
          pending && (
            <PermissionGate perm={MANAGE_PERM}>
              <Button size="small" variant="contained" onClick={() => setDialog('approve')}>
                Approve…
              </Button>
              <Button size="small" variant="outlined" onClick={() => setDialog('reject')}>
                Reject…
              </Button>
              <Button size="small" onClick={() => setDialog('withdraw')}>
                Withdraw…
              </Button>
            </PermissionGate>
          )
        }
      >
        {r ? (
          <>
            <LifecycleRequestQuote request={r} />
            {r.status !== 'pending' && (
              <Box sx={{ mt: 0.5 }}>
                {jobOf(r) ? (
                  'Approved; the job below applies it.'
                ) : (
                  <>
                    Approved; {r.status === 'applying' ? 'a job is applying it' : 'it is being applied'}
                    {r.result?.task_id ? ` (task ${r.result.task_id})` : ''}.
                  </>
                )}
              </Box>
            )}
          </>
        ) : (
          'A close, a code release or a reopen is asked for here first, then approved — by someone else when two-person approval is on.'
        )}
      </StateRow>
      <ApproveLifecycleDialog
        topId={state.tour_operator_id}
        code={state.code}
        request={target('approve')}
        twoPersonRequired={state.two_person_required}
        lifecycle={state.lifecycle}
        onClose={() => setDialog(null)}
      />
      <RejectLifecycleDialog
        topId={state.tour_operator_id}
        request={target('reject')}
        onClose={() => setDialog(null)}
      />
      <WithdrawLifecycleDialog
        topId={state.tour_operator_id}
        request={target('withdraw')}
        onClose={() => setDialog(null)}
      />
    </>
  );
}

// --------------------------------------------------------------------------- job

const JOB_WHAT = { release: 'releases the code', restore: 'gives the tenant its code back' } as const;

function JobRow({ state, follow }: { state: TenantLifecycleState; follow?: JobFollow }) {
  const [retrying, setRetrying] = useState(false);
  const r = currentJobRequest(state);
  const job = r ? jobOf(r) : null;
  if (!r || !job) return null;
  const failed = r.status === 'failed';
  const at = job.step == null ? -1 : job.steps.indexOf(job.step);
  const of = `${at + 1} of ${job.steps.length}`;
  const chip = failed ? (
    <StateChip tone="error" label={at >= 0 ? `Failed at step ${of}` : 'Failed'} />
  ) : (
    <StateChip tone="info" label={at >= 0 ? `Running — step ${of}` : 'Starting'} />
  );
  const taskId = r.result?.task_id;
  return (
    <>
      <StateRow
        icon={<EngineeringOutlinedIcon fontSize="small" />}
        label="Job"
        chip={chip}
        actions={
          // A failed job runs again from its step; a running one only if its worker died (it
          // stays applying, and the backend refuses while the job is alive).
          <PermissionGate perm={MANAGE_PERM}>
            <Button size="small" variant={failed ? 'contained' : 'text'} onClick={() => setRetrying(true)}>
              Retry…
            </Button>
          </PermissionGate>
        }
      >
        A job {JOB_WHAT[job.kind]} (request #{r.id}
        {taskId && (
          <>
            ,{' '}
            <Link component={RouterLink} to={`/bgtasks/${taskId}`}>
              background task
            </Link>
          </>
        )}
        ).{' '}
        {failed
          ? `It stopped; the tenant stays ${state.lifecycle}, so its code stays taken until the job is run again.`
          : follow?.stopped
            ? null
            : 'This page follows it.'}
        {!failed && follow?.stopped && (
          <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <span>Still running — this page stopped following it after a while.</span>
            <Button size="small" variant="outlined" onClick={follow.onResume}>
              Refresh
            </Button>
          </Box>
        )}
        {!failed && follow?.failing && (
          <Box sx={{ mt: 0.5, color: 'warning.main' }} role="status">
            Couldn’t read the job’s progress just now — trying again, less often. What shows is from the
            last read.
          </Box>
        )}
        {failed && r.result?.error && (
          <Box
            sx={{ mt: 0.5, color: 'error.main', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
            data-job-error
          >
            {r.result.error}
          </Box>
        )}
        <LifecycleJobSteps request={r} job={job} />
      </StateRow>
      <RetryLifecycleDialog
        topId={state.tour_operator_id}
        request={retrying ? r : null}
        onClose={() => setRetrying(false)}
      />
    </>
  );
}

// --------------------------------------------------------------------------- approval

function ApprovalRow({ state }: { state: TenantLifecycleState }) {
  return (
    <StateRow
      icon={<GroupOutlinedIcon fontSize="small" />}
      label="Approval"
      chip={
        state.two_person_required ? (
          <StateChip tone="info" label="Two-person" />
        ) : (
          <StateChip tone="default" label="Requester may approve" />
        )
      }
    >
      {state.two_person_required
        ? 'The requester cannot approve their own request: a second ops user approves it.'
        : 'The requester may approve their own request — still two steps: request, then approve.'}
    </StateRow>
  );
}

// --------------------------------------------------------------------------- release gate

function ReleaseGateRow({ state }: { state: TenantLifecycleState }) {
  const [asking, setAsking] = useState(false);
  const earliest = state.earliest_release_at;
  const waiting = earliest != null && new Date(earliest).getTime() > Date.now();
  const chip =
    state.lifecycle === 'releasing' ? (
      <StateChip tone="info" label="Release running" />
    ) : state.lifecycle === 'released' ? (
      <StateChip tone="default" label="Released" />
    ) : state.lifecycle !== 'closed' ? (
      <StateChip tone="default" label="Not closed" />
    ) : waiting ? (
      <StateChip tone="warning" label="Waiting period" />
    ) : (
      <StateChip tone="success" label="Waiting period over" />
    );
  // Only a closed tenant can release; any other cannot at all, which the chip already says.
  const closed = state.lifecycle === 'closed';
  return (
    <>
      <StateRow
        icon={<ScheduleOutlinedIcon fontSize="small" />}
        label="Release gate"
        chip={chip}
        actions={
          closed &&
          state.open_request == null && (
            <PermissionGate perm={MANAGE_PERM}>
              <Button size="small" variant="outlined" color="error" onClick={() => setAsking(true)}>
                Request release…
              </Button>
            </PermissionGate>
          )
        }
      >
        A code release needs the tenant closed for {state.release_min_closed_days} days
        {earliest ? (
          <>
            {' '}
            — {waiting ? 'not before' : 'possible since'} <When iso={earliest} />
          </>
        ) : null}
        .
        {closed && (
          <LifecycleBlockerList
            topId={state.tour_operator_id}
            blockers={state.release_blockers}
            label="What blocks a release now"
          />
        )}
      </StateRow>
      <RequestLifecycleDialog
        state={state}
        kind={asking ? 'release' : null}
        onClose={() => setAsking(false)}
      />
    </>
  );
}
